import { useMutation, useQuery } from '@apollo/client';
import {
  CALCULATE_CART_TAX,
  GET_TAX_CHECKOUT_CAPABILITY,
} from '@retainerclub/customer-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  capabilityFailure,
  commercialDependencyKey,
  normalizeCapability,
  normalizeShippingAddress,
  normalizeUserErrors,
  quoteIsUnexpired,
  requestFailure,
} from './helpers/checkoutTaxQuote';

const QUOTE_DEBOUNCE_MILLISECONDS = 300;

function emptyQuoteState(status = 'idle', errors = []) {
  return { quoteId: null, expiresAt: null, status, errors };
}

export default function useCheckoutTaxQuote({
  cart,
  cartId,
  shippingAddress,
  deliveryMethod,
  addressValid,
  updateCartLocal,
  paused = false,
}) {
  const [capability, setCapability] = useState(null);
  const [quoteState, setQuoteState] = useState(() => emptyQuoteState());
  const { quoteId, expiresAt, status, errors } = quoteState;
  const requestNumberRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const dependencyKeyRef = useRef(null);
  const mountedRef = useRef(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const {
    data: capabilityData,
    error: capabilityError,
    loading: capabilityLoading,
    refetch: refetchCapability,
  } = useQuery(GET_TAX_CHECKOUT_CAPABILITY, {
    fetchPolicy: 'network-only',
    skip: !(cartId && !paused),
    variables: { cartId },
  });
  const [calculateCartTax] = useMutation(CALCULATE_CART_TAX, {
    fetchPolicy: 'no-cache',
  });

  const shippingAddressId = shippingAddress?.id;
  const shippingAddressLine = shippingAddress?.address;
  const shippingAddressLine2 = shippingAddress?.address2;
  const shippingAddressCity = shippingAddress?.city;
  const shippingAddressCountry = shippingAddress?.country;
  const shippingAddressIsDefault = shippingAddress?.isDefault;
  const shippingAddressPostalCode = shippingAddress?.postalCode;
  const shippingAddressRegion = shippingAddress?.region;
  const normalizedShippingAddress = useMemo(
    () =>
      normalizeShippingAddress({
        id: shippingAddressId,
        address: shippingAddressLine,
        address2: shippingAddressLine2,
        city: shippingAddressCity,
        country: shippingAddressCountry,
        isDefault: shippingAddressIsDefault,
        postalCode: shippingAddressPostalCode,
        region: shippingAddressRegion,
      }),
    [
      shippingAddressCity,
      shippingAddressCountry,
      shippingAddressId,
      shippingAddressIsDefault,
      shippingAddressLine,
      shippingAddressLine2,
      shippingAddressPostalCode,
      shippingAddressRegion,
    ],
  );

  const dependencyKey = useMemo(
    () =>
      commercialDependencyKey({
        cart,
        cartId,
        deliveryMethod,
        shippingAddress: normalizedShippingAddress,
      }),
    [cart, cartId, deliveryMethod, normalizedShippingAddress],
  );
  dependencyKeyRef.current = dependencyKey;

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const resetQuote = useCallback((nextStatus = 'idle') => {
    setQuoteState(emptyQuoteState(nextStatus));
  }, []);

  const invalidate = useCallback(() => {
    clearDebounceTimer();
    requestNumberRef.current += 1;
    resetQuote();
  }, [clearDebounceTimer, resetQuote]);

  const applyQuoteError = useCallback((nextErrors) => {
    setQuoteState(emptyQuoteState('error', nextErrors));
  }, []);

  const applyQuotePayload = useCallback(
    (payload) => {
      const nextCapability = normalizeCapability(payload?.capability);
      const nextErrors = normalizeUserErrors(payload?.errorMessages);

      if (nextCapability) {
        setCapability(nextCapability);
      }

      if (nextErrors.length > 0) {
        applyQuoteError(nextErrors);
        return null;
      }

      if (!(nextCapability && payload?.cart)) {
        applyQuoteError(requestFailure('INVALID_TAX_QUOTE_RESPONSE'));
        return null;
      }

      if (nextCapability?.enabled === false) {
        if (typeof updateCartLocal === 'function') {
          updateCartLocal(payload.cart);
        }
        resetQuote('ready');
        return payload;
      }

      if (!(payload?.taxQuoteId && quoteIsUnexpired(payload.expiresAt))) {
        applyQuoteError(requestFailure('INVALID_TAX_QUOTE_RESPONSE'));
        return null;
      }

      if (typeof updateCartLocal === 'function') {
        updateCartLocal(payload.cart);
      }
      setQuoteState({
        quoteId: payload.taxQuoteId,
        expiresAt: payload.expiresAt,
        status: 'ready',
        errors: [],
      });
      return payload;
    },
    [applyQuoteError, resetQuote, updateCartLocal],
  );

  const requestQuote = useCallback(async () => {
    const checkoutDisabled = capability?.enabled === false;
    const checkoutEnabled = capability?.enabled === true;
    const canRequest =
      checkoutDisabled ||
      (checkoutEnabled && addressValid && Boolean(deliveryMethod));

    if (paused || !cartId || !canRequest) {
      return null;
    }

    const requestNumber = ++requestNumberRef.current;
    const requestedDependencyKey = dependencyKeyRef.current;
    setQuoteState(emptyQuoteState('calculating'));

    try {
      const response = await calculateCartTax({
        variables: {
          input: {
            input: {
              cartId,
              deliveryMethod: deliveryMethod || '',
              shippingAddress: normalizedShippingAddress,
            },
          },
        },
      });

      if (
        !mountedRef.current ||
        pausedRef.current ||
        requestNumber !== requestNumberRef.current ||
        requestedDependencyKey !== dependencyKeyRef.current
      ) {
        return null;
      }

      return applyQuotePayload(response?.data?.calculateCartTax);
    } catch (_error) {
      if (
        !mountedRef.current ||
        pausedRef.current ||
        requestNumber !== requestNumberRef.current ||
        requestedDependencyKey !== dependencyKeyRef.current
      ) {
        return null;
      }

      applyQuoteError(requestFailure());
      return null;
    }
  }, [
    addressValid,
    applyQuoteError,
    applyQuotePayload,
    calculateCartTax,
    capability?.enabled,
    cartId,
    deliveryMethod,
    normalizedShippingAddress,
    paused,
  ]);

  const refresh = useCallback(async () => {
    clearDebounceTimer();

    if (paused) {
      return null;
    }

    if (!capability) {
      resetQuote();
      try {
        await refetchCapability();
      } catch (_error) {
        if (mountedRef.current && !pausedRef.current) {
          applyQuoteError(capabilityFailure());
        }
      }
      return null;
    }

    if (capability?.enabled === false) {
      return requestQuote();
    }

    if (!(capability?.enabled && addressValid && deliveryMethod)) {
      invalidate();
      return null;
    }

    return requestQuote();
  }, [
    addressValid,
    applyQuoteError,
    capability,
    capability?.enabled,
    clearDebounceTimer,
    deliveryMethod,
    invalidate,
    paused,
    refetchCapability,
    requestQuote,
    resetQuote,
  ]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestNumberRef.current += 1;
      clearDebounceTimer();
    };
  }, [clearDebounceTimer]);

  useEffect(() => {
    requestNumberRef.current += 1;
    clearDebounceTimer();
    setCapability(null);
    resetQuote();

    if (!cartId || capabilityLoading) {
      return;
    }

    const nextCapability = normalizeCapability(
      capabilityData?.taxCheckoutCapability,
    );
    if (capabilityError || !nextCapability) {
      setQuoteState(emptyQuoteState('error', capabilityFailure()));
      return;
    }

    setCapability(nextCapability);
    resetQuote();
  }, [
    capabilityData?.taxCheckoutCapability,
    capabilityError,
    capabilityLoading,
    cartId,
    clearDebounceTimer,
    resetQuote,
  ]);

  useEffect(() => {
    clearDebounceTimer();
    requestNumberRef.current += 1;

    if (paused) {
      return;
    }

    resetQuote();

    if (
      !(dependencyKey && typeof capability?.enabled === 'boolean' && cartId)
    ) {
      return;
    }

    if (capability.enabled === false) {
      requestQuote();
      return;
    }

    if (!(addressValid && deliveryMethod)) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      requestQuote();
    }, QUOTE_DEBOUNCE_MILLISECONDS);

    return clearDebounceTimer;
  }, [
    addressValid,
    capability?.enabled,
    cartId,
    clearDebounceTimer,
    deliveryMethod,
    dependencyKey,
    paused,
    requestQuote,
    resetQuote,
  ]);

  useEffect(() => {
    if (
      paused ||
      !capability?.enabled ||
      status !== 'ready' ||
      !quoteId ||
      !quoteIsUnexpired(expiresAt)
    ) {
      return;
    }

    const remainingMilliseconds = Date.parse(expiresAt) - Date.now();
    const expirationTimer = setTimeout(() => {
      if (mountedRef.current) {
        requestQuote();
      }
    }, remainingMilliseconds + 1);

    return () => clearTimeout(expirationTimer);
  }, [capability?.enabled, expiresAt, paused, quoteId, requestQuote, status]);

  const isReady =
    (capability?.enabled === false &&
      status === 'ready' &&
      Number(cart?.taxAmount || 0) === 0) ||
    (capability?.enabled === true &&
      status === 'ready' &&
      Boolean(quoteId) &&
      quoteIsUnexpired(expiresAt));

  return {
    capability,
    quoteId,
    expiresAt,
    status,
    errors,
    isReady,
    invalidate,
    refresh,
  };
}
